import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDay } from './amlDates';
import './Aml.css';
import AmlNav from './AmlNav';
import useAmlFramework from './useAmlFramework';
import { canManageAml } from './amlPermissions';
import { listAmlRisks, createAmlRisk, advanceAmlRisk, updateAmlRisk, linkAmlRiskControl, listAmlControls, reopenAmlRisk, getAmlRiskHistory, deleteAmlRisk, listAmlRiskTemplates, createAmlRiskFromTemplate } from './amlApi';

const FACTORS = ['CUSTOMER', 'PRODUCT', 'CHANNEL', 'GEOGRAPHY'];
// by/at/ref name the risk fields holding who recorded the step, when, and the minutes reference.
const STEPS = [
  { key: 'PREPARED', label: 'PREPARED', sub: 'Oficial de Cumplimiento', by: 'preparedBy', at: 'preparedAt' },
  { key: 'REVIEWED', label: 'REVIEWED', sub: 'Comité de Cumplimiento', by: 'reviewedBy', at: 'reviewedAt', ref: 'reviewReference' },
  { key: 'APPROVED', label: 'APPROVED', sub: 'Consejo de Administración', by: 'approvedBy', at: 'approvedAt', ref: 'approvalReference' },
];
const STEP_ORDER = ['DRAFT', 'PREPARED', 'REVIEWED', 'APPROVED'];

const bandClass = (band) => (band === 'HIGH' ? 'aml-pill-high' : band === 'MEDIUM' ? 'aml-pill-medium' : 'aml-pill-low');
// Scores sit on a 5 x 5 matrix. min/max on a number input do not stop a typed value, so the forms
// check with this before saving.
const inRange = (value, max) => Number.isInteger(value) && value >= 1 && value <= max;

export default function AmlRiskAssessment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = canManageAml();
  const { status, frameworks, frameworkId, setFrameworkId } = useAmlFramework();
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [factorFilter, setFactorFilter] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [usingTemplateId, setUsingTemplateId] = useState(null);
  const [controlsOpenId, setControlsOpenId] = useState(null);
  const [form, setForm] = useState({ description: '', factor: 'CUSTOMER', likelihood: 3, impact: 3, owner: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [controlIdInput, setControlIdInput] = useState('');
  const [controls, setControls] = useState([]);
  const [linkError, setLinkError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [minutesRef, setMinutesRef] = useState('');
  const [advancing, setAdvancing] = useState(false);
  const [advanceError, setAdvanceError] = useState('');
  const currentUser = (localStorage.getItem('userName') || '').toLowerCase();

  // The review and approval steps are decisions of the Comité / Consejo, so they need the minutes
  // reference; the user who prepared a risk cannot record its approval (two-person rule).
  const needsMinutes = (r) => r.status === 'PREPARED' || r.status === 'REVIEWED';
  const isOwnApproval = (r) => r.status === 'REVIEWED' && !!r.preparedBy && r.preparedBy.toLowerCase() === currentUser;

  const load = () => {
    if (!frameworkId) return;
    setLoading(true);
    listAmlRisks(frameworkId).then((rows) => { setRisks(rows); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [frameworkId]);

  // The framework's controls, for the picker that links a control to a risk.
  useEffect(() => {
    if (!frameworkId) return;
    listAmlControls(frameworkId).then(setControls).catch(() => setControls([]));
  }, [frameworkId]);

  const loadTemplates = () => {
    if (!frameworkId || !showTemplates) return;
    setTemplatesLoading(true);
    setTemplateError('');
    listAmlRiskTemplates(frameworkId)
      .then((ts) => { setTemplates(ts); setTemplatesLoading(false); })
      .catch(() => { setTemplates([]); setTemplatesLoading(false); });
  };
  useEffect(loadTemplates, [frameworkId, showTemplates]);

  const applyTemplate = async (tpl) => {
    setUsingTemplateId(tpl.id);
    setTemplateError('');
    try {
      await createAmlRiskFromTemplate(frameworkId, tpl.id);
      setTemplates((ts) => ts.map((x) => (x.id === tpl.id ? { ...x, used: true } : x)));
      load();
    } catch (e) {
      setTemplateError(e?.response?.data?.message || t('Aml.templateUseFailed', 'Could not add this template to the register.'));
    } finally {
      setUsingTemplateId(null);
    }
  };

  // Advisory coloring only, so it uses the default bands rather than another round-trip.
  const suggestedBand = (score) => (score >= 15 ? 'HIGH' : score >= 8 ? 'MEDIUM' : 'LOW');

  // A control is shown by its reference and name; the internal number only when it has neither.
  const controlLabel = (c) => [c.reference, c.name].filter(Boolean).join(' · ') || `#${c.controlId ?? c.id}`;

  const visible = factorFilter === 'ALL' ? risks : risks.filter((r) => r.factor === factorFilter);
  const counts = { HIGH: 0, MEDIUM: 0, LOW: 0 };
  risks.forEach((r) => { counts[r.residualBand] = (counts[r.residualBand] || 0) + 1; });

  const submitAdd = async () => {
    if (!form.description.trim()) { setError(t('Aml.descriptionRequired', 'A description is required.')); return; }
    if (!inRange(form.likelihood, 5) || !inRange(form.impact, 5)) {
      setError(t('Aml.scaleRange', 'Likelihood and impact must be whole numbers from 1 to 5.'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createAmlRisk(frameworkId, form);
      setShowAdd(false);
      setForm({ description: '', factor: 'CUSTOMER', likelihood: 3, impact: 3, owner: '' });
      load();
    } catch (e) {
      setError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the risk.'));
    } finally {
      setSaving(false);
    }
  };

  const refreshHistory = (id) => {
    setHistoryLoading(true);
    getAmlRiskHistory(frameworkId, id).then((h) => { setHistory(h); setHistoryLoading(false); }).catch(() => setHistoryLoading(false));
  };

  const advance = async (r) => {
    if (needsMinutes(r) && !minutesRef.trim()) {
      setAdvanceError(t('Aml.minutesRequired', 'Enter the minutes reference of the session.'));
      return;
    }
    setAdvancing(true);
    setAdvanceError('');
    try {
      await advanceAmlRisk(frameworkId, r.id, needsMinutes(r) ? minutesRef.trim() : undefined);
      setMinutesRef('');
      load();
      if (expandedId === r.id) refreshHistory(r.id);
    } catch (e) {
      setAdvanceError(e?.response?.data?.message || t('Aml.advanceFailed', 'Could not record this step.'));
    } finally {
      setAdvancing(false);
    }
  };

  const openExpand = (r) => {
    if (expandedId === r.id) { setExpandedId(null); return; }
    setExpandedId(r.id);
    setEditForm({ likelihood: r.likelihood, impact: r.impact, residualScore: r.residualScore,
      owner: r.owner || '', treatment: r.treatment || '' });
    setEditError('');
    setControlIdInput('');
    setLinkError('');
    setMinutesRef('');
    setAdvanceError('');
    setHistory([]);
    if (r.version > 1 || r.locked) refreshHistory(r.id);
  };

  const reopenForNewVersion = async (id) => {
    setReopening(true);
    try {
      await reopenAmlRisk(frameworkId, id);
      load();
      if (expandedId === id) refreshHistory(id);
    } finally {
      setReopening(false);
    }
  };

  const saveEdit = async (id) => {
    if (!inRange(editForm.likelihood, 5) || !inRange(editForm.impact, 5)) {
      setEditError(t('Aml.scaleRange', 'Likelihood and impact must be whole numbers from 1 to 5.'));
      return;
    }
    if (!inRange(editForm.residualScore, 25)) {
      setEditError(t('Aml.residualRange', 'Residual score must be a whole number from 1 to 25.'));
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      await updateAmlRisk(frameworkId, id, editForm);
      load();
    } catch (e) {
      setEditError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save changes.'));
    } finally {
      setEditSaving(false);
    }
  };

  const removeRisk = async (r) => {
    if (!window.confirm(t('Aml.deleteRiskConfirm', 'Delete risk {{key}}? This cannot be undone.', { key: r.riskKey }))) return;
    setEditError('');
    try {
      await deleteAmlRisk(frameworkId, r.id);
      setExpandedId(null);
      load();
    } catch (e) {
      setEditError(e?.response?.data?.message || t('Aml.deleteFailed', 'Could not delete.'));
    }
  };

  const linkControl = async (id) => {
    const controlId = Number(controlIdInput);
    if (!controlId) return;
    setLinkError('');
    try {
      await linkAmlRiskControl(frameworkId, id, controlId);
      setControlIdInput('');
      load();
    } catch (e) {
      setLinkError(e?.response?.data?.message || t('Aml.linkFailed', 'Could not link the control.'));
    }
  };

  if (status === 'loading' || loading) return <div className="aml-page"><div className="aml-spinner" /></div>;

  if (status === 'error') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.loadError', 'Could not load the AML Program. Your session may have expired — try reloading.')}
        </div>
      </div>
    );
  }

  if (status === 'not-enrolled') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.notEnrolled', 'Your organization has not enrolled in an AML framework yet. Enroll in SEPS-IGT-2025-0113 or another AML framework from the Frameworks page to activate the AML Program.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aml-btn aml-btn-primary" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('Aml.goToFrameworks', 'Go to Frameworks')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aml-page">
      <div className="aml-header">
        <div>
          <h1 className="aml-title">{t('Aml.riskTitle', 'AML Risk Assessment')}</h1>
          <p className="aml-subtitle">
            {t('Aml.riskSubtitle', 'ML/TF risks by factor (customers, products, channels, geography), scored and approved as required.')}
          </p>
        </div>
      </div>

      <div className="aml-stat-bar">
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.high', 'HIGH')}</div><div className="aml-stat-val aml-val-bad">{counts.HIGH}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.medium', 'MEDIUM')}</div><div className="aml-stat-val">{counts.MEDIUM}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.low', 'LOW')}</div><div className="aml-stat-val">{counts.LOW}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.total', 'TOTAL')}</div><div className="aml-stat-val">{risks.length}</div></div>
      </div>

      <AmlNav frameworks={frameworks} frameworkId={frameworkId} onFrameworkChange={setFrameworkId} />

      <div className="aml-toolbar">
        <div className="aml-toolbar-group">
          <label htmlFor="aml-factor" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>{t('Aml.riskFactor', 'Risk factor')}</label>
          <select id="aml-factor" className="aml-select" value={factorFilter} onChange={(e) => setFactorFilter(e.target.value)}>
            <option value="ALL">{t('Aml.allFactors', 'All factors')}</option>
            {FACTORS.map((f) => <option key={f} value={f}>{f.charAt(0) + f.slice(1).toLowerCase()}</option>)}
          </select>
        </div>
        {canManage && (
          <div className="aml-toolbar-group">
            <button className="aml-btn" onClick={() => { setShowTemplates((s) => !s); setShowAdd(false); }}>
              {showTemplates ? t('Aml.hideTemplates', 'Hide templates') : t('Aml.addFromTemplate', '✨ Add from template')}
            </button>
            <button className="aml-btn aml-btn-primary" onClick={() => { setShowAdd((s) => !s); setShowTemplates(false); }}>{t('Aml.addRisk', '+ Add risk')}</button>
          </div>
        )}
      </div>

      {showTemplates && (
        <div className="aml-section">
          <div className="aml-tpl-hint">
            {t('Aml.templatesHint', 'Starter risks curated for this framework. Using one creates a draft in your register — adjust the scores to your own reality, then route it for approval like any other risk.')}
          </div>
          {templateError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', padding: '0.5rem 1rem 0' }}>{templateError}</div>}
          {templatesLoading ? (
            <div className="aml-spinner" />
          ) : templates.length === 0 ? (
            <div className="aml-empty">{t('Aml.templatesEmpty', 'No templates are available for this framework.')}</div>
          ) : (
            <div className="aml-tpl-grid">
              {templates.map((tpl) => {
                const score = tpl.suggestedLikelihood * tpl.suggestedImpact;
                return (
                  <div key={tpl.id} className={`aml-tpl-card${tpl.used ? ' aml-tpl-card-used' : ''}`}>
                    <div className="aml-tpl-head">
                      <span className="aml-tpl-title">{tpl.title}</span>
                      <span className="aml-badge aml-pill-muted">{tpl.factor}</span>
                    </div>
                    <div className="aml-tpl-desc">{tpl.description}</div>
                    {(tpl.controls || []).length > 0 && (
                      <button type="button" className="aml-tpl-ctl-toggle"
                        onClick={() => setControlsOpenId(controlsOpenId === tpl.id ? null : tpl.id)}>
                        {controlsOpenId === tpl.id
                          ? t('Aml.hideControls', 'Hide controls ▴')
                          : t('Aml.showControls', '{{count}} mitigating controls ▾', { count: tpl.controls.length })}
                      </button>
                    )}
                    {controlsOpenId === tpl.id && (
                      <div className="aml-tpl-chips">
                        {tpl.controls.map((c) => (
                          <span key={c.reference} className="aml-badge-mono">
                            {c.reference}{c.name ? ` · ${c.name}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="aml-tpl-foot">
                      <span className={`aml-pill ${bandClass(suggestedBand(score))}`}>
                        {t('Aml.suggested', 'Suggested')} {tpl.suggestedLikelihood} × {tpl.suggestedImpact} = {score}
                      </span>
                      {tpl.used ? (
                        <span className="aml-pill aml-pill-low">✓ {t('Aml.templateAdded', 'In register')}</span>
                      ) : (
                        <button className="aml-btn aml-btn-accent" style={{ height: 32, padding: '0 0.75rem' }}
                          disabled={usingTemplateId === tpl.id} onClick={() => applyTemplate(tpl)}>
                          {usingTemplateId === tpl.id ? t('Aml.adding', 'Adding…') : t('Aml.useTemplate', 'Use template')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showAdd && (
        <div className="aml-section" style={{ padding: '1rem' }}>
          {error && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            <label>
              <div className="aml-expand-label">{t('Aml.description', 'Description')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.factor', 'Factor')}</div>
              <select className="aml-select" style={{ width: '100%' }} value={form.factor}
                onChange={(e) => setForm({ ...form, factor: e.target.value })}>
                {FACTORS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.likelihood', 'Likelihood (1-5)')}</div>
              <input type="number" min={1} max={5} className="aml-input" style={{ width: '100%' }} value={form.likelihood}
                onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.impact', 'Impact (1-5)')}</div>
              <input type="number" min={1} max={5} className="aml-input" style={{ width: '100%' }} value={form.impact}
                onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.owner', 'Owner')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.owner}
                onChange={(e) => setForm({ ...form, owner: e.target.value })} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
            <button className="aml-btn aml-btn-primary" disabled={saving} onClick={submitAdd}>{t('Aml.save', 'Save')}</button>
            <button className="aml-btn" onClick={() => setShowAdd(false)}>{t('Aml.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}

      <div className="aml-section">
        <div className="aml-table-wrap">
          <table className="aml-table">
            <thead>
              <tr>
                <th>{t('Aml.key', 'KEY')}</th><th>{t('Aml.risk', 'RISK')}</th><th>{t('Aml.factorCol', 'FACTOR')}</th>
                <th>{t('Aml.inherent', 'INHERENT')}</th><th>{t('Aml.residual', 'RESIDUAL')}</th>
                <th>{t('Aml.ownerCol', 'OWNER')}</th><th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={7} className="aml-empty">{t('Aml.noRisks', 'No risks yet. Add one to start the assessment.')}</td></tr>
              )}
              {visible.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td><span className="aml-badge-mono">{r.riskKey}</span>{r.version > 1 && <span className="aml-muted" style={{ fontSize: '0.6875rem' }}> v{r.version}</span>}</td>
                    <td style={{ fontWeight: 600 }}>{r.description}</td>
                    <td><span className="aml-badge aml-pill-muted">{r.factor}</span></td>
                    <td><span className={`aml-pill ${bandClass(r.inherentBand)}`}>{r.inherentBand} · {r.inherentScore}</span></td>
                    <td><span className={`aml-pill ${bandClass(r.residualBand)}`}>{r.residualBand} · {r.residualScore}</span></td>
                    <td>{r.owner || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="aml-btn" style={{ height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
                        onClick={() => openExpand(r)}>
                        {expandedId === r.id ? t('Aml.hide', 'Hide ▴') : t('Aml.view', 'View ▾')}
                      </button>
                    </td>
                  </tr>
                  {expandedId === r.id && editForm && (
                    <tr className="aml-expand-row">
                      <td colSpan={7}>
                        {/* An approved risk, and any risk for a user who cannot edit, shows as a summary. */}
                        {r.locked || !canManage ? (
                          <div className="aml-expand-grid">
                            <div><div className="aml-expand-label">{t('Aml.likelihoodImpact', 'LIKELIHOOD × IMPACT')}</div>{r.likelihood} × {r.impact}</div>
                            <div><div className="aml-expand-label">{t('Aml.linkedControls', 'LINKED CONTROLS')}</div>
                              {r.linkedControls?.length ? r.linkedControls.map(controlLabel).join(', ') : t('Aml.none', 'None linked')}
                            </div>
                            <div><div className="aml-expand-label">{t('Aml.treatmentCol', 'TREATMENT')}</div>{r.treatment || '—'}</div>
                          </div>
                        ) : (
                          <>
                            {editError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{editError}</div>}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.625rem' }}>
                              <label>
                                <div className="aml-expand-label">{t('Aml.likelihood', 'Likelihood (1-5)')}</div>
                                <input type="number" min={1} max={5} className="aml-input" style={{ width: '100%' }} value={editForm.likelihood}
                                  onChange={(e) => setEditForm({ ...editForm, likelihood: Number(e.target.value) })} />
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.impact', 'Impact (1-5)')}</div>
                                <input type="number" min={1} max={5} className="aml-input" style={{ width: '100%' }} value={editForm.impact}
                                  onChange={(e) => setEditForm({ ...editForm, impact: Number(e.target.value) })} />
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.residualScore', 'Residual score')}</div>
                                <input type="number" min={1} max={25} className="aml-input" style={{ width: '100%' }} value={editForm.residualScore}
                                  onChange={(e) => setEditForm({ ...editForm, residualScore: Number(e.target.value) })} />
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.owner', 'Owner')}</div>
                                <input className="aml-input" style={{ width: '100%' }} value={editForm.owner}
                                  onChange={(e) => setEditForm({ ...editForm, owner: e.target.value })} />
                              </label>
                              <label style={{ gridColumn: '1 / -1' }}>
                                <div className="aml-expand-label">{t('Aml.treatment', 'Treatment')}</div>
                                <input className="aml-input" style={{ width: '100%' }} value={editForm.treatment}
                                  onChange={(e) => setEditForm({ ...editForm, treatment: e.target.value })} />
                              </label>
                            </div>
                            <button className="aml-btn aml-btn-primary" style={{ marginTop: '0.625rem' }} disabled={editSaving}
                              onClick={() => saveEdit(r.id)}>{t('Aml.save', 'Save')}</button>
                            {r.status !== 'DRAFT' && (
                              <span className="aml-muted" style={{ fontSize: '0.75rem', marginLeft: '0.625rem' }}>
                                {t('Aml.saveResetsApproval', 'Saving changes sends this risk back to draft.')}
                              </span>
                            )}
                            {/* A risk that was approved once keeps its signed history and cannot be deleted. */}
                            {r.version === 1 && (
                              <button className="aml-btn" style={{ marginTop: '0.625rem', marginLeft: '0.625rem', color: '#b91c1c' }}
                                onClick={() => removeRisk(r)}>{t('Aml.deleteRisk', 'Delete risk')}</button>
                            )}

                            <div style={{ marginTop: '1rem' }}>
                              <div className="aml-expand-label">{t('Aml.linkedControls', 'LINKED CONTROLS')}</div>
                              <div style={{ marginTop: '0.25rem' }}>
                                {r.linkedControls?.length ? r.linkedControls.map(controlLabel).join(', ') : t('Aml.none', 'None linked')}
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
                                <select className="aml-select" style={{ maxWidth: 420 }} aria-label={t('Aml.controlToLink', 'Control to link')}
                                  value={controlIdInput} onChange={(e) => setControlIdInput(e.target.value)}>
                                  <option value="">{t('Aml.chooseControl', 'Choose a control…')}</option>
                                  {controls.filter((c) => !r.linkedControls?.some((l) => l.controlId === c.id)).map((c) => (
                                    <option key={c.id} value={c.id}>{controlLabel(c)}</option>
                                  ))}
                                </select>
                                <button className="aml-btn" disabled={!controlIdInput} onClick={() => linkControl(r.id)}>{t('Aml.link', 'Link')}</button>
                              </div>
                              {linkError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginTop: '0.375rem' }}>{linkError}</div>}
                            </div>
                          </>
                        )}
                        <div style={{ marginTop: '1rem' }}>
                          <div className="aml-expand-label">{t('Aml.approval', 'APPROVAL')}</div>
                          <div className="aml-stepper" style={{ marginTop: '0.5rem' }}>
                            {STEPS.map((s, i) => {
                              const done = STEP_ORDER.indexOf(r.status) > STEP_ORDER.indexOf(STEPS[i - 1]?.key || 'DRAFT') && STEP_ORDER.indexOf(r.status) >= STEP_ORDER.indexOf(s.key);
                              return (
                                <React.Fragment key={s.key}>
                                  {i > 0 && <div className="aml-step-connector" />}
                                  <div className="aml-step">
                                    <span className={`aml-step-dot ${done ? 'aml-step-dot-done' : 'aml-step-dot-pending'}`}>{done ? '✓' : i + 1}</span>
                                    <span className="aml-step-label" style={{ color: done ? '#1d4ed8' : '#64748b' }}>{t(`Aml.step${s.key}`, s.label)}</span>
                                    <span className="aml-step-sub">{s.sub}</span>
                                    {done && r[s.by] && (
                                      <span className="aml-step-sub">
                                        {r[s.by]}{r[s.at] ? ` · ${formatDay(r[s.at])}` : ''}
                                      </span>
                                    )}
                                    {done && s.ref && r[s.ref] && (
                                      <span className="aml-step-sub">{t('Aml.minutes', 'Minutes')}: {r[s.ref]}</span>
                                    )}
                                  </div>
                                </React.Fragment>
                              );
                            })}
                          </div>
                          {!r.locked && canManage && (
                            <div style={{ marginTop: '0.75rem' }}>
                              {advanceError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{advanceError}</div>}
                              {isOwnApproval(r) && (
                                <div className="aml-muted" style={{ fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                                  {t('Aml.ownApprovalBlocked', 'You prepared this risk, so another administrator must record its approval.')}
                                </div>
                              )}
                              {needsMinutes(r) && !isOwnApproval(r) && (
                                <label style={{ display: 'block', maxWidth: 420, marginBottom: '0.5rem' }}>
                                  <div className="aml-expand-label">
                                    {r.status === 'PREPARED' ? t('Aml.reviewMinutes', 'Comité minutes reference') : t('Aml.approvalMinutes', 'Consejo minutes reference')}
                                  </div>
                                  <input className="aml-input" style={{ width: '100%' }} maxLength={255}
                                    placeholder={t('Aml.minutesPlaceholder', 'e.g. Acta 14, 2 Oct 2026')}
                                    value={minutesRef} onChange={(e) => setMinutesRef(e.target.value)} />
                                </label>
                              )}
                              <button className="aml-btn aml-btn-accent" disabled={advancing || isOwnApproval(r)} onClick={() => advance(r)}>
                                {r.status === 'DRAFT' ? t('Aml.markPrepared', 'Mark prepared')
                                  : r.status === 'PREPARED' ? t('Aml.recordReview', 'Record Comité review')
                                  : t('Aml.recordApproval', 'Record Consejo approval')}
                              </button>
                            </div>
                          )}
                          {r.locked && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <div className="aml-muted" style={{ fontSize: '0.75rem' }}>
                                {t('Aml.lockedNote', 'Approved and locked.')}
                                {r.contentSignature && (
                                  <span style={{ fontFamily: 'monospace', marginLeft: '0.375rem' }}>
                                    {t('Aml.signature', 'Signature')}: {r.contentSignature.slice(0, 16)}…
                                  </span>
                                )}
                              </div>
                              {canManage && (
                                <button className="aml-btn" style={{ marginTop: '0.5rem', height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
                                  disabled={reopening} onClick={() => reopenForNewVersion(r.id)}>
                                  {t('Aml.reopenForNewVersion', 'Reopen for new version')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {(history.length > 0 || historyLoading) && (
                          <div style={{ marginTop: '1rem' }}>
                            <div className="aml-expand-label">{t('Aml.versionHistory', 'VERSION HISTORY (YEAR-OVER-YEAR)')}</div>
                            {historyLoading ? (
                              <div className="aml-muted" style={{ fontSize: '0.8125rem', marginTop: '0.375rem' }}>{t('Aml.loading', 'Loading…')}</div>
                            ) : (
                              <table className="aml-table" style={{ marginTop: '0.375rem' }}>
                                <thead>
                                  <tr>
                                    <th>{t('Aml.versionCol', 'VERSION')}</th><th>{t('Aml.residual', 'RESIDUAL')}</th>
                                    <th>{t('Aml.approvedAtCol', 'APPROVED')}</th>
                                    <th>{t('Aml.preparedByCol', 'PREPARED BY')}</th><th>{t('Aml.approvedByCol', 'APPROVED BY')}</th>
                                    <th>{t('Aml.minutesCol', 'MINUTES')}</th><th>{t('Aml.signature', 'SIGNATURE')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {history.map((h) => (
                                    <tr key={h.version}>
                                      <td>v{h.version}</td>
                                      <td>{h.residualScore}</td>
                                      <td>{formatDay(h.approvedAt)}</td>
                                      <td>{h.preparedBy || '—'}</td>
                                      <td>{h.approvedBy || '—'}</td>
                                      <td>{h.approvalReference || '—'}</td>
                                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{h.contentSignature?.slice(0, 16)}…</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="aml-table-foot">
          <span>{t('Aml.showing', '{{count}} of {{total}}', { count: visible.length, total: risks.length })}</span>
          <span>{t('Aml.matrixNote', 'Scored on the same 5 × 5 matrix as AI Governance')}</span>
        </div>
      </div>
    </div>
  );
}
